import { FileUp, Download, Upload, Package } from 'lucide-react';

const AdminProducts = ({
  products,
  brandCategories,
  selectedFile,
  setSelectedFile,
  selectedBrandForUpload,
  onBrandChange,
  selectedCategoryForUpload,
  setSelectedCategoryForUpload,
  handleFileUpload,
  downloadTemplate,
  productSearch,
  setProductSearch,
  filterBrand,
  setFilterBrand,
  filterCategory,
  setFilterCategory,
}) => {
  const filteredProducts = products.filter((product) => {
    const searchLower = productSearch.toLowerCase();
    const matchesSearch =
      !productSearch ||
      product.sku.toLowerCase().includes(searchLower) ||
      (product.description && product.description.toLowerCase().includes(searchLower));
    const matchesBrand = filterBrand === 'All' || product.brand === filterBrand;
    const matchesCategory = filterCategory === 'All' || product.category === filterCategory;
    return matchesSearch && matchesBrand && matchesCategory;
  });

  return (
    <div className="space-y-6" data-testid="admin-products">
      {/* Upload Section */}
      <div className="bg-black/40 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Upload className="text-ewss-cyan" size={24} />
          Upload Product Catalog
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Select Brand *</label>
            <select
              value={selectedBrandForUpload}
              onChange={(e) => onBrandChange(e.target.value)}
              className="w-full px-4 py-3 bg-ewss-navy border border-gray-600 rounded-lg text-white"
              data-testid="admin-products-upload-brand-select"
            >
              <option value="Haiwell">Haiwell</option>
              <option value="Supmea">Supmea</option>
              <option value="Savch">Savch</option>
              <option value="Yotta Control">Yotta Control</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Select Category *</label>
            <select
              value={selectedCategoryForUpload}
              onChange={(e) => setSelectedCategoryForUpload(e.target.value)}
              className="w-full px-4 py-3 bg-ewss-navy border border-gray-600 rounded-lg text-white"
              data-testid="admin-products-upload-category-select"
            >
              <option value="">-- Choose Category --</option>
              {brandCategories[selectedBrandForUpload]?.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">Categories for {selectedBrandForUpload}</p>
          </div>
        </div>

        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-300 mb-2">Select Excel File *</label>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setSelectedFile(e.target.files[0])}
              className="w-full px-4 py-3 bg-ewss-navy border border-gray-600 rounded-lg text-white"
              data-testid="admin-products-file-input"
            />
          </div>
          <button
            onClick={handleFileUpload}
            className="px-6 py-3 bg-ewss-cyan text-ewss-navy font-semibold rounded-lg hover:bg-ewss-cyan/90 transition-colors flex items-center gap-2"
            data-testid="admin-products-upload-button"
          >
            <FileUp size={18} />
            Upload
          </button>
          <button
            onClick={downloadTemplate}
            className="px-6 py-3 bg-gray-700 text-white font-semibold rounded-lg hover:bg-gray-600 transition-colors flex items-center gap-2"
            data-testid="admin-products-template-button"
          >
            <Download size={18} />
            Template
          </button>
        </div>
      </div>

      {/* Products List */}
      <div className="bg-black/40 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Package className="text-ewss-cyan" size={24} />
          Current Products ({products.length})
        </h3>

        {products.length === 0 ? (
          <p className="text-gray-400">No products uploaded yet</p>
        ) : (
          <>
            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div>
                <label className="text-gray-400 text-sm mb-2 block">Search Products</label>
                <input
                  type="text"
                  placeholder="Search SKU, name, description..."
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  className="w-full px-4 py-2 bg-ewss-navy border border-gray-600 rounded-lg text-white placeholder-gray-500"
                  data-testid="admin-products-search-input"
                />
              </div>
              <div>
                <label className="text-gray-400 text-sm mb-2 block">Filter by Brand</label>
                <select
                  value={filterBrand}
                  onChange={(e) => setFilterBrand(e.target.value)}
                  className="w-full px-4 py-2 bg-ewss-navy border border-gray-600 rounded-lg text-white"
                  data-testid="admin-products-filter-brand"
                >
                  <option value="All">All Brands</option>
                  {[...new Set(products.map((p) => p.brand))].sort().map((brand) => (
                    <option key={brand} value={brand}>
                      {brand}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-gray-400 text-sm mb-2 block">Filter by Category</label>
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="w-full px-4 py-2 bg-ewss-navy border border-gray-600 rounded-lg text-white"
                  data-testid="admin-products-filter-category"
                >
                  <option value="All">All Categories</option>
                  {[...new Set(products.map((p) => p.category))].sort().map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto max-h-[600px] overflow-y-auto border border-gray-700 rounded-lg">
              <table className="w-full">
                <thead className="sticky top-0 bg-ewss-navy z-10">
                  <tr className="border-b border-gray-700 text-left">
                    <th className="p-3 text-gray-400 font-medium">SKU</th>
                    <th className="p-3 text-gray-400 font-medium">Description</th>
                    <th className="p-3 text-gray-400 font-medium">Brand</th>
                    <th className="p-3 text-gray-400 font-medium">Category</th>
                    <th className="p-3 text-gray-400 font-medium">Subcategory</th>
                    <th className="p-3 text-gray-400 font-medium">Price (Excl)</th>
                    <th className="p-3 text-gray-400 font-medium">Price (Incl)</th>
                    <th className="p-3 text-gray-400 font-medium">Stock</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((product) => (
                    <tr
                      key={product.id}
                      className="border-b border-gray-700/50 hover:bg-gray-800/30"
                    >
                      <td className="p-3 text-white font-mono text-sm">{product.sku}</td>
                      <td className="p-3 text-white">{product.description || '-'}</td>
                      <td className="p-3 text-gray-400">{product.brand}</td>
                      <td className="p-3 text-gray-400">{product.category}</td>
                      <td className="p-3 text-gray-400 text-sm">{product.subcategory || '-'}</td>
                      <td className="p-3 text-white">
                        R{' '}
                        {(product.base_price_excl || product.base_price || 0).toLocaleString(
                          'en-ZA',
                          { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                        )}
                      </td>
                      <td className="p-3 text-ewss-cyan">
                        R{' '}
                        {(
                          product.base_price_incl ||
                          product.base_price * 1.15 ||
                          0
                        ).toLocaleString('en-ZA', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td className="p-3">
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${
                            product.stock_status === 'In Stock'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-yellow-500/20 text-yellow-400'
                          }`}
                        >
                          {product.stock_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 text-gray-400 text-sm">
              Showing {filteredProducts.length} of {products.length} products
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminProducts;
